import { ArrowRightIcon, CheckboxTickIcon, FilterIcon } from 'assets/icons';
import { FC, useEffect, useState } from 'react';
import { VoidFn } from 'types/baseTypes';
import { PAYOUT_TXNS_AMOUNT_FILTERS, PAYOUT_TXNS_TABS, PAYOUT_TXNS_TYPE_FILTERS } from '../constants';
import { CustomButton, CustomCheckbox, If } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import AmountFilter from './AmountFilterBox';
import { getPayoutTransactionFilters } from '../utils';

interface PayoutTransactionsFiltersProps {
  handleClose: VoidFn;
  currentSelectedTxnTab: PAYOUT_TXNS_TABS;
  onApply: (filter: Record<string, any>) => void;
  appliedAmountFilter: Record<string, any>;
  appliedTypeFilters: PAYOUT_TXNS_TYPE_FILTERS[];
}

const PayoutTransactionsFilters: FC<PayoutTransactionsFiltersProps> = ({
  handleClose,
  currentSelectedTxnTab,
  onApply,
  appliedAmountFilter,
  appliedTypeFilters
}) => {
  const availableFilters = getPayoutTransactionFilters(currentSelectedTxnTab);

  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [amountFilter, setAmountFilter] = useState({
    title: appliedAmountFilter.title || PAYOUT_TXNS_AMOUNT_FILTERS.ALL,
    min: appliedAmountFilter.min || '',
    max: appliedAmountFilter.max || ''
  });

  useEffect(() => {
    if (appliedTypeFilters.length > 0) {
      setTypeFilters(appliedTypeFilters);
    }
  }, [appliedTypeFilters]);

  const isSelected = filter => {
    return typeFilters?.includes(filter);
  };

  const addTypeFilter = (filter: string) => {
    const currentTypeFilters = [...typeFilters];
    if (isSelected(filter)) {
      const index = currentTypeFilters.findIndex(item => item === filter);
      currentTypeFilters.splice(index, 1);
    } else {
      currentTypeFilters.push(filter);
    }
    setTypeFilters(currentTypeFilters);
  };

  const onApplyFilters = () => {
    onApply({ type: typeFilters, amount: amountFilter });
  };

  const applyBtnDisabled = () => {
    if (amountFilter.title !== PAYOUT_TXNS_AMOUNT_FILTERS.ALL) {
      if (amountFilter.title === PAYOUT_TXNS_AMOUNT_FILTERS.IN_BETWEEN) {
        if (+amountFilter.max <= +amountFilter.min) {
          return true;
        }
        return false;
      } else if (!amountFilter.min) {
        return true;
      }
      return false;
    }
    return false;
  };

  return (
    <div className="flex h-full w-96 flex-col">
      <div className="flex items-center gap-2 px-3.5 pt-6">
        <div onClick={handleClose} className="cursor-pointer">
          <ArrowRightIcon className="rotate-180" />
        </div>
        <div className="flex items-center gap-2.5 text-17px font-semibold text-headingGray">
          <FilterIcon />
          Filter
        </div>
      </div>
      <div className="mx-[30px] mt-4 mb-5 border-b border-borderGray"></div>

      <div className="relative mx-[30px]">
        <div className="text-sbase font-semibold text-primaryText">By amount</div>
        <AmountFilter selectedFilter={amountFilter} onFilterChange={setAmountFilter} />
      </div>

      {currentSelectedTxnTab === PAYOUT_TXNS_TABS.ALL && (
        <div className="mx-[30px] mt-5">
          <div className="text-sbase font-semibold text-primaryText">By type</div>
          <div className="flex flex-wrap gap-2.5 py-5">
            {availableFilters.map(filter => (
              <div
                className={`flex min-w-[82px] shrink-0 cursor-pointer items-center gap-2.5 rounded-[60px] border py-2 
              px-4 text-headingGray
              ${isSelected(filter.key) ? 'border-secondary' : 'border-borderGray'}`}
                onClick={() => addTypeFilter(filter.key)}
                key={filter.key}>
                <CustomCheckbox
                  id={`payout-txn-typ-${filter}`}
                  isChecked={isSelected(filter.key)}
                  onClick={() => addTypeFilter(filter.key)}>
                  <If condition={isSelected(filter.key)}>
                    <CheckboxTickIcon />
                  </If>
                </CustomCheckbox>
                <div className="text-center text-sbase font-semibold capitalize">{filter.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-auto flex gap-5 bg-secondaryBtn px-4 py-2.5">
        <CustomButton
          type={ButtonType.SECONDARY}
          id="payout-txns-filter-cancel"
          className="h-[38px] w-1/2"
          onClick={handleClose}>
          Cancel
        </CustomButton>
        <CustomButton
          type={ButtonType.PRIMARY}
          id="payout-txns-filter-apply"
          className="h-[38px] w-1/2"
          onClick={onApplyFilters}
          disabled={applyBtnDisabled()}>
          Apply
        </CustomButton>
      </div>
    </div>
  );
};

export default PayoutTransactionsFilters;
